import { type RefObject, useEffect, useState } from 'react';

import { type FontWidthMap } from './chars-font-width-map.js';
import { truncateString } from './truncate-string.js';

/**
 * Hook that truncates text in the middle with an ellipsis based on the width of the container element if needed.
 * It's recalculated on container resize using a ResizeObserver.
 * @param text
 * @param charsFontWidthMap
 * @param containerRef
 */
export const useTruncateMiddleEllipsis = (
  text: string,
  charsFontWidthMap: FontWidthMap,
  containerRef: RefObject<HTMLSpanElement>,
) => {
  const [displayText, setDisplayText] = useState(text);

  useEffect(() => {
    const containerEl = containerRef.current;
    if (!containerEl) {
      return;
    }

    const checkOverflow = (availableWidth: number) => {
      const truncatedString = truncateString(
        text,
        availableWidth,
        charsFontWidthMap,
      );
      setDisplayText(truncatedString);
    };

    const resizeObserver = new ResizeObserver((entries) => {
      const entry = entries[0];
      const containerWidth = entry.contentRect.width;
      requestAnimationFrame(() => checkOverflow(containerWidth));
    });

    resizeObserver.observe(containerEl);

    return () => {
      resizeObserver.disconnect();
    };
  }, [text, charsFontWidthMap]);

  return displayText;
};
